import React, { useContext } from "react";
import { Helmet } from "react-helmet";
import { graphql } from "gatsby";
import { LayoutContainer } from "../containers/layout";
import PageHeadMeta from "../components/global/page-head-meta";
import { mapEdgesToNodes, toPlainText, imageBuilder } from "../lib/helpers";
import { CardContext } from "../components/card/context";
import { suggest } from "../lib/suggestions";
import { ResourcesSuggestions, SectionWrapper } from "../components/sections";
import { Chatbot } from "../components/sections/chatbot";
import PortableText from "../components/portableText";
import { format } from "date-fns";
import { CardGrid } from "../components/card/card-grid";
import { pageDocPath } from "../../helpers";
import { SearchPopup } from "../components/sections/snowflake/search";
import { DocumentationMenu } from "../components/sections/snowflake/docs-menu";
import { ImageBuilder } from "../components/global/image-builder";
import { SiteContext } from "../components/global/site-context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsSplitUpAndLeft, faList } from "@fortawesome/free-solid-svg-icons";
import { EyebrowPill } from "../components/serializers";
import { Button } from "../components/button";

import * as styles from "../components/portableText.module.css";

export const query = graphql`
  query DocumentationTemplateQuery($id: String!, $integrations: [String!]) {
    doc: sanityDocumentation(id: { eq: $id }) {
      _type
      _id
      id
      _updatedAt
      _createdAt
      menu
      menuParent
      categories {
        _id
        _type
        title
        titlePublic
        slug {
          current
        }
        status
        mainImage {
          ...SanityImage
          alt
        }
      }
      title
      menuTitle
      slug {
        current
      }
      status
      _rawExcerpt(resolveReferences: { maxDepth: 5 })
      _rawBody(resolveReferences: { maxDepth: 12 })
      integrations {
        ...Integration
      }
      suggestions {
        ... on SanityPost {
          ...cardNode
        }
        ... on SanityAsset {
          ...cardNodeAsset
        }
        ... on SanityPage {
          ...cardNodePage
        }
        ... on SanityPlaybook {
          ...cardNodePlaybook
        }
        ... on SanityDocumentation {
          ...cardNodeDocumentation
        }
      }
    }
    docs: allSanityDocumentation(
      sort: { fields: [orderRank] }
      filter: { menu: { ne: null }, status: { eq: "published" } }
    ) {
      edges {
        node {
          _type
          id
          _id
          menu
          menuParent
          title
          menuTitle
          slug {
            current
          }
          integrations {
            _id
          }
        }
      }
    }
    playbooks: allSanityPlaybook(
      limit: 3
      sort: { fields: [_createdAt], order: DESC }
      filter: { integrations: { elemMatch: { _id: { in: $integrations } } } }
    ) {
      edges {
        node {
          ...cardNodePlaybook
        }
      }
    }
    integrations: allSanityIntegration {
      edges {
        node {
          _id
          docRef {
            _id
          }
          mainImageSolid {
            ...SanityImageSimple
          }
          integrationsSimilar {
            company
            mainImageSolid {
              ...SanityImageSimple
            }
            docRef {
              _id
              _type
              slug {
                current
              }
              menu
              menuParent
            }
          }
          integrationsPairs {
            company
            mainImageSolid {
              ...SanityImageSimple
            }
            docRef {
              _id
              _type
              slug {
                current
              }
              menu
              menuParent
            }
          }
        }
      }
    }
    supportBot: sanityAgent(_id: { eq: "7cf4bfbb-c006-4793-978d-e443ffdef59a" }) {
      ...Agent
    }
  }
`;

const DocumentationTemplate = ({ data, location }) => {
  const docs = mapEdgesToNodes(data?.docs);

  return (
    <LayoutContainer doc={data?.doc} location={location}>
      <PageHeadMeta
        title={`${data?.doc.title} | Common Room (Docs)`}
        isTitleOverriden={true}
        description={toPlainText(data?.doc._rawExcerpt)}
        image={data?.doc.mainImage}
        doc={data?.doc}
      />
      <CardContext.Provider value={{ ...data?.doc }}>
        {data?.doc && (
          <DocumentationPage
            {...data?.doc}
            suggest={suggest(data)}
            docs={docs}
            playbooks={mapEdgesToNodes(data?.playbooks)}
            integration={mapEdgesToNodes(data?.integrations).find(
              (i) => i._id === data?.doc?.integrations?.[0]?._id
            )}
            integrationsLenth={data?.doc?.integrations?.length}
            relatedDocs={docs.filter(
              (d) =>
                data?.doc?.integrations?.[0]?._id &&
                d._id !== data?.doc?._id &&
                d.integrations?.[0]?._id === data?.doc?.integrations?.[0]?._id
            )}
          />

        )}
      </CardContext.Provider>
    </LayoutContainer>
  );
};

export default DocumentationTemplate;

const generateIntegrationsListSchema = (integration, relatedIntegrations) => {
  const integrations = [];

  // Add the main integration if it exists
  if (integration?.company) {
    integrations.push(integration);
  }

  // Add related integrations
  if (relatedIntegrations?.length > 0) {
    relatedIntegrations.forEach(relInt => {
      if (relInt?.company && relInt?.docRef) {
        integrations.push(relInt);
      }
    });
  }

  if (integrations.length === 0) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": integrations.map((int, index) => {
      const item = {
        "@type": "SoftwareApplication",
        "name": int.company,
        "applicationCategory": "BusinessApplication",
      };

      if (int.mainImageSolid?.asset) {
        const logoUrl = imageBuilder(int.mainImageSolid)?.height(200)?.url();
        if (logoUrl) item.image = logoUrl;
      }

      if (int._rawExcerpt) {
        const description = toPlainText(int._rawExcerpt);
        if (description) item.offers = description;
      }

      return {
        "@type": "ListItem",
        "position": index + 1,
        "item": item,
      };
    }),
  };

  return schema;
};

const DocumentationPage = ({
  title,
  menu,
  _rawBody,
  categories,
  _updatedAt,
  docs,
  suggestions,
  suggest,
  playbooks,
  menuParent,
  integration,
  relatedDocs,
  integrationsLenth,
  supportBot,
  menuTitle,
}) => {
  const site = useContext(SiteContext);
  const isHome = site.isActive("suggestions") && menu === "get-started" && menuParent;
  const relatedIntegrations = integration
    ? integration?.integrationsSimilar.concat(integration?.integrationsPairs)
    : [];
  const integrationsListSchema = generateIntegrationsListSchema(integration, relatedIntegrations);

  return isHome ? <DocsHome docs={docs} agent={supportBot} /> : (
    <>
      {integrationsListSchema && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(integrationsListSchema)}
          </script>
        </Helmet>
      )}
      <SectionWrapper>
        <div className="grid relative xl:grid-cols-menu-content-toc">
          <div className="mb-10 mt-12 md:mt-0">
            <div className="p-4 pb-8 bg-dark-5 rounded-2xl">
              <SearchPopup />
              <div className="mt-8">
                <DocumentationMenu docs={docs} menu={menu} />
              </div>
            </div>
            <Button
              className="mt-6 hover:bg-black hover:text-white"
              onClick={() => {
                // Scroll to the chat anchor
                const chatAnchor = document.querySelector('[data-chat-anchor]');
                if (chatAnchor) {
                  const elementPosition = chatAnchor.getBoundingClientRect().top + window.pageYOffset;
                  window.scrollTo({
                    top: elementPosition - 300,
                    behavior: 'smooth'
                  });
                }
              }}
              text="Ask support bot"
              bgColor="bg-gray-400"
            />
          </div>
          <div className="pt-2 mb-16 xl:mb-0 md:xl:mt-4 md:mx-12" id="article">
            <h1
              className="font-semibold tracking-tight flex items-center"
              style={{ fontSize: "1.5rem" }}
            >
              {integration && integrationsLenth === 1 && (
                <ImageBuilder
                  image={integration.mainImageSolid}
                  width={36}
                  className="mr-2 rounded-md"
                  alt={`${title} logo`}
                  quality={100}
                />
              )}
              {title}
            </h1>
            <p className="mt-2 mb-8 text-sm text-auxiliary" id="updated">
              Last updated {format(new Date(_updatedAt), "MMM do, yyyy")}
            </p>
            <div className="leading-relaxed">
              {_rawBody && (
                <PortableText blocks={_rawBody} className={styles.docsBody} />
              )}
            </div>

            <div className="mt-10 lg:mt-16 border-t border-dark-5 pt-8">
              <EyebrowPill className="mb-4" isSpecial={true}>
                Support chat
              </EyebrowPill>
              <Chatbot
                agent={{
                  ...supportBot,
                  role: `Embed on website! Your page context is: ${title}`,
                }}
                firstMessage={`How else can I help with ${menuTitle || title}?`}
                showSupport={true}
                minimal={true}
                whiteBg={true}
                skipAutoFocus={true}
              />
            </div>
          </div>
          <div className="md:mt-8 hidden xl:block">
            <div
              className="sticky top-28 left-0 text-secondary"
              style={{ fontSize: 13 }}
            >
              {_rawBody &&
                _rawBody?.filter((b) => ["h2"].includes(b.style)).length >
                2 && (
                  <div className="mb-8">
                    <a className="mb-2 font-semibold block" href="#top">
                      <FontAwesomeIcon
                        icon={faList}
                        className="mr-1"
                      />
                      On this page
                    </a>
                    {_rawBody && <TOC body={_rawBody} />}
                  </div>
                )}
              {relatedDocs.length > 0 && (
                <div className="mb-8">
                  <h3 className="mt-6 mb-2 font-medium">Related Docs</h3>
                  <ul className="text-sm">
                    {relatedDocs.map((d, i) => (
                      <li key={i}>
                        <a
                          href={pageDocPath(d)}
                          className="ml-1 pl-3 py-2 border-l border-dark-10 hover:border-link hover:text-link block"
                        >
                          {d.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {playbooks.length > 0 && (
                <div className="mb-8">
                  <h3 className="mt-6 mb-2 font-medium">Related Playbooks</h3>
                  <ul className="text-sm">
                    {playbooks.map((p, i) => (
                      <li key={i}>
                        <a
                          href={pageDocPath(p)}
                          className="ml-1 pl-3 py-2 border-l border-dark-10 hover:border-link hover:text-link block"
                        >
                          {p.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm mt-2">
                    <a href="/playbooks/" className="hover:text-link">
                      See all playbooks
                    </a>
                  </p>
                </div>
              )}
              {relatedIntegrations.length > 0 && (
                <>
                  <h3 className="mb-4 font-medium">Related Integrations</h3>
                  <ul className="flex ml-1 pl-3 border-l border-dark-10">
                    {relatedIntegrations.map((i, n) =>
                      i.docRef ? (
                        <li className="mr-2" key={n}>
                          <a
                            key={n}
                            href={pageDocPath(i.docRef)}
                            className="inline-block transform transition-size scale-100 hover:scale-110 group mr-0.75"
                            title={i.company}
                          >
                            <ImageBuilder
                              image={i.mainImageSolid}
                              width={28}
                              className="inline-block relative -top-px rounded-md saturate-0 group-hover:saturate-100"
                              alt={`${i.company} logo`}
                              quality={100}
                            />
                          </a>
                        </li>
                      ) : null
                    )}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      </SectionWrapper>

      <div className="hidden xl:block">
        <SectionWrapper
          header="Fresh playbooks"
          layout="centered"
          container="large"
          background="lavenderUltraLight"
        >
          {suggestions.length > 0 ? (
            <CardGrid
              nodes={suggestions}
              panels={true}
              makeThumbnail={true}
              browseMoreHref="/resources/"
              browseMoreText="See all resources"
              attribution={{ action: "suggestion", label: "playbook" }}
              maxColumns={3}
              hideType={true}
            />
          ) : (
            <ResourcesSuggestions section={{ cards: 6 }} maxColumns={3} />
          )}
        </SectionWrapper>
      </div>
    </>
  );
};

const TOC = ({ body }) => (
  <ul className="text-sm">
    {body
      .filter((b) => ["h2"].includes(b.style))
      .map((b, i) => (
        <li
          className="ml-1 last:pb-0 border-l border-dark-10 hover:border-link"
          key={i}
        >
          <a
            href={`#${tocId(b.children[0]?.text)}`}
            className="hover:text-link py-2 block ml-3"
          >
            {b.children[0].text}
          </a>
        </li>
      ))}
  </ul>
);

export const tocId = (text) =>
  typeof text === "string"
    ? text.toLowerCase().replace(/[\W_]+/g, "-")
    : text?.props
      ? text?.props.node.children[0].toLowerCase().replace(/[\W_]+/g, "-")
      : null;

const DocsHome = ({ menu, docs, agent }) => {
  const site = useContext(SiteContext);

  return (
    <SectionWrapper padding="tight">
      <div className="grid relative xl:grid-cols-menu-content-toc-full mb-16 mt-4">
        <div className="mb-10 mt-12">
          <div className="p-4 pb-12 bg-dark-5 rounded-2xl">
            <SearchPopup />
            <div className="mt-8">
              <DocumentationMenu docs={docs} menu={menu} />
            </div>
          </div>
        </div>
        <div className="mt-16 pt-1.5 mb-16 xl:mb-0 md:mx-12" id="article">
          <h1 className="text-3xl font-medium tracking-tight mb-4">
            {site.token("docsHomeTitle")}
          </h1>
          <Chatbot
            copilotID={agent?.copilot_id}
            workflowID={agent?.workflow_id}
            agent={{
              role: `${agent?.role} Your page context is: ${site.token("docsHomeTitle")}`,
              tasks: agent?.tasks,
              interject: toPlainText(agent?._rawInterject),
              answers: toPlainText(agent?._rawAnswers),
              questions: agent?.questions,
              steps:
                agent?.steps.map(s => ({
                  goal: s.goal,
                  response: s._rawResponse,
                  smarts: s.smarts || "normal",
                  achievement: s.achievement
                }))
            }}
            showSupport={true}
            whiteBg={true}
          />
          <div>
            <h3 className="text-xl mb-6 flex gap-1 items-center mt-8 lg:mt-16">
              <FontAwesomeIcon icon={faArrowsSplitUpAndLeft} size="xs" className="max-h-4" />
              Fresh playbooks
            </h3>
            <ResourcesSuggestions
              section={{ cards: site.isNotMobile ? 6 : 3 }}
              maxColumns={3}
              browseMoreHref="/resources/playbooks/"
              browseMoreText="All playbooks"
            />
          </div>
        </div>
      </div>
    </SectionWrapper>
  )
};
